// src/index.ts
import Cookies from "js-cookie";
function interpolate(str, ctx) {
  return str.replace(/{{\s?([a-zA-Z]+)\s?}}/g, (match, key) => ctx[key] || match);
}
function isObject(value) {
  const type = typeof value;
  return value != null && (type === "object" || type === "function");
}
var configs = {
  default: {
    apiUrl: "/api",
    signOutUrl: "/auth/sign_out",
    emailSignInPath: "/auth/sign_in",
    emailRegistrationPath: "/auth",
    accountUpdatePath: "/auth",
    accountDeletePath: "/auth",
    confirmationSuccessUrl() {
      return window.location.href;
    },
    passwordResetPath: "/auth/password",
    passwordUpdatePath: "/auth/password",
    passwordResetSuccessUrl() {
      return window.location.href;
    },
    tokenValidationPath: "/auth/validate_token",
    proxyIf() {
      return false;
    },
    proxyUrl: "/proxy",
    validateOnPageLoad: true,
    omniauthWindowType: "sameWindow",
    storage: "cookies",
    transport: "cookies",
    forceValidateToken: false,
    tokenFormat: {
      "access-token": "{{ token }}",
      "token-type": "Bearer",
      client: "{{ clientId }}",
      expiry: "{{ expiry }}",
      uid: "{{ uid }}"
    },
    cookieOps: {
      path: "/",
      expires: 9999,
      secure: false
    },
    createPopup(url) {
      return window.open(url, "_blank", "closebuttoncaption=Cancel");
    },
    parseExpiry(headers) {
      return parseInt(headers.expiry, 10) * 1e3 || null;
    },
    handleLoginResponse(resp) {
      return resp.data;
    },
    handleAccountUpdateResponse(resp) {
      return resp.data;
    },
    handleTokenValidationResponse(resp) {
      return resp.data;
    },
    authProviderPaths: {
      google_oauth2: "/auth/google_oauth2",
      facebook: "/auth/facebook",
      apple_quantic: "/auth/apple_quantic",
      apple_smartly: "/auth/apple_smartly",
      twitter: "/auth/twitter",
      onelogin: "/auth/onelogin",
      wechat: "/auth/wechat",
      wechat_official_account: "/auth/wechat_official_account"
    },
    httpWrapper: typeof window !== "undefined" && window.fetch != null ? window.fetch : void 0,
    broadcast(name, payload) {
      if (process.env.NODE_ENV === "development") {
        console.log(`[${name}]:`, payload);
      }
    },
    navigate(url, replace) {
      if (replace) {
        return window.location.replace(url);
      }
      return window.location.assign(url);
    }
  }
};
var defaultConfigName = "default";
var DeviseTokenAuthClient = class {
  constructor(params) {
    this.dfd = null;
    this.user = {};
    this.headers = {};
    this.mustResetPassword = false;
    this.firstTimeLogin = false;
    this.oauthRegistration = false;
    this.timer = null;
    this._hasSessionStorage = null;
    this._hasLocalStorage = null;
    this.listener = null;
    this.requestCredentialsPollingTimer = null;
    this.cancelOmniauthInAppBrowserListeners = null;
    if (params instanceof Array && params.length) {
      throw new Error("DeviceTokenAuthClient config as an Array is not supported.\nWe need to migrate cloneDeep from lodash/fp/cloneDeep first.");
    } else if (params instanceof Object) {
      Object.assign(configs.default, params);
    } else {
      throw new Error("Invalid argument: DeviceTokenAuthClient config should be an Array or Object.");
    }
  }
  http(input, init) {
    const httpWrapper = this.getConfig().httpWrapper;
    if (!httpWrapper) {
      throw new Error("No httpWrapper configured");
    }
    return httpWrapper(input, init).then(
      (res) => res.json().then((data) => {
        const response = { data, status: res.status };
        return res.ok ? response : Promise.reject(response);
      })
    );
  }
  initialize() {
    this.initializeListeners();
    this.cancelOmniauthInAppBrowserListeners = () => {
    };
    const currHeaders = this.retrieveData("auth_headers") || {};
    Object.assign(this.headers, currHeaders);
    if (this.getConfig().validateOnPageLoad) {
      this.validateUser({ config: this.getSavedConfig() });
    }
  }
  initializeListeners() {
    this.listener = this.handlePostMessage.bind(this);
    if (window.addEventListener) {
      window.addEventListener("message", this.listener, false);
    }
  }
  cancel(reason) {
    if (this.requestCredentialsPollingTimer != null) {
      window.clearTimeout(this.requestCredentialsPollingTimer);
    }
    if (this.cancelOmniauthInAppBrowserListeners) {
      this.cancelOmniauthInAppBrowserListeners();
    }
    if (this.dfd != null) {
      this.rejectDfd(reason);
    }
    window.setTimeout(() => {
      this.requestCredentialsPollingTimer = null;
    });
  }
  destroy() {
    this.cancel();
    if (window.removeEventListener) {
      window.removeEventListener("message", this.listener, false);
    }
  }
  handlePostMessage(ev) {
    const config = this.getConfig();
    if (ev.data.message === "deliverCredentials") {
      delete ev.data.message;
      const oauthRegistration = ev.data.oauth_registration;
      this.handleValidAuth(ev.data, true);
      config.broadcast("auth:login-success", ev.data);
      if (oauthRegistration) {
        config.broadcast("auth:oauth-registration", ev.data);
      }
    }
    if (ev.data.message === "authFailure") {
      const error = {
        reason: "unauthorized",
        errors: [ev.data.error]
      };
      this.cancel(error);
      config.broadcast("auth:login-error", error);
    }
  }
  submitRegistration(params, opts = {}) {
    const config = this.getConfig(opts.config);
    return this.http(this.apiUrl(opts.config) + config.emailRegistrationPath, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        ...params,
        confirm_success_url: this.getResultOrValue(config.confirmationSuccessUrl),
        config_name: this.getCurrentConfigName(opts.config)
      })
    }).then(
      (resp) => {
        config.broadcast("auth:registration-email-success", params);
        return resp;
      },
      (resp) => {
        config.broadcast("auth:registration-email-error", resp.data);
        throw resp;
      }
    );
  }
  submitLogin(params, opts = {}) {
    const promise = this.initDfd();
    const config = this.getConfig(opts.config);
    this.http(this.apiUrl(opts.config) + config.emailSignInPath, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(params)
    }).then(
      (resp) => {
        this.setConfigName(opts.config);
        const authData = config.handleLoginResponse(resp.data);
        this.handleValidAuth(authData);
        config.broadcast("auth:login-success", this.user);
      },
      (resp) => {
        this.rejectDfd({
          reason: "unauthorized",
          errors: resp.data ? resp.data.errors : ["Invalid credentials"]
        });
        config.broadcast("auth:login-error", resp.data);
      }
    );
    return promise;
  }
  userIsAuthenticated() {
    return this.retrieveData("auth_headers") && this.user.signedIn && !this.tokenHasExpired();
  }
  requestPasswordReset(params, opts = {}) {
    const config = this.getConfig(opts.config);
    params.redirect_url = this.getResultOrValue(config.passwordResetSuccessUrl);
    if (opts.config != null) {
      params.config_name = opts.config;
    }
    return this.http(this.apiUrl(opts.config) + config.passwordResetPath, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(params)
    }).then(
      (resp) => {
        config.broadcast("auth:password-reset-request-success", params);
        return resp;
      },
      (resp) => {
        config.broadcast("auth:password-reset-request-error", resp.data);
        throw resp;
      }
    );
  }
  updatePassword(params, opts = {}) {
    const config = this.getConfig(opts.config);
    return this.http(this.apiUrl(opts.config) + config.passwordUpdatePath, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(params)
    }).then(
      (resp) => {
        config.broadcast("auth:password-change-success", resp.data);
        this.mustResetPassword = false;
        return resp;
      },
      (resp) => {
        config.broadcast("auth:password-change-error", resp.data);
        throw resp;
      }
    );
  }
  updateAccount(params, opts = {}) {
    const config = this.getConfig(opts.config);
    return this.http(this.apiUrl(opts.config) + config.accountUpdatePath, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(params)
    }).then(
      (resp) => {
        const updateResponse = config.handleAccountUpdateResponse(resp.data);
        const curHeaders = this.retrieveData("auth_headers");
        Object.assign(this.user, updateResponse);
        if (curHeaders) {
          const newHeaders = {};
          Object.entries(config.tokenFormat).forEach(([key]) => {
            if (curHeaders[key] && updateResponse[key]) {
              newHeaders[key] = updateResponse[key];
            }
          });
          this.setAuthHeaders(newHeaders);
        }
        config.broadcast("auth:account-update-success", resp.data);
        return resp;
      },
      (resp) => {
        config.broadcast("auth:account-update-error", resp.data);
        throw resp;
      }
    );
  }
  destroyAccount(params, opts = {}) {
    const config = this.getConfig(opts.config);
    return this.http(this.apiUrl(opts.config) + config.accountUpdatePath, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(params)
    }).then(
      (resp) => {
        this.invalidateTokens();
        config.broadcast("auth:account-destroy-success", resp.data);
        return resp;
      },
      (resp) => {
        config.broadcast("auth:account-destroy-error", resp.data);
        throw resp;
      }
    );
  }
  authenticate(provider, opts = {}) {
    if (this.dfd == null) {
      this.setConfigName(opts.config);
      const promise = this.initDfd();
      this.openAuthWindow(provider, opts);
      return promise;
    }
    return this.dfd.promise;
  }
  setConfigName(configName) {
    if (configName == null) {
      configName = defaultConfigName;
    }
    return this.persistData("currentConfigName", configName, configName);
  }
  openAuthWindow(provider, opts = {}) {
    const { omniauthWindowType, createPopup } = this.getConfig(opts.config);
    const authUrl = this.buildAuthUrl(omniauthWindowType, provider, opts);
    if (omniauthWindowType === "newWindow") {
      this.requestCredentialsViaPostMessage(createPopup(authUrl));
    } else if (omniauthWindowType === "inAppBrowser") {
      this.requestCredentialsViaExecuteScript(createPopup(authUrl));
    } else if (omniauthWindowType === "sameWindow") {
      this.visitUrl(authUrl);
    } else {
      throw new Error(`Unsupported omniauthWindowType "${omniauthWindowType}"`);
    }
  }
  visitUrl(url) {
    return this.getConfig().navigate(url, true);
  }
  buildAuthUrl(omniauthWindowType, provider, opts = {}) {
    const { apiUrl, authProviderPaths } = this.getConfig(opts.config);
    const authUrl = new URL(apiUrl + authProviderPaths[provider]);
    const params = {
      auth_origin_url: opts.auth_origin_url || window.location.href,
      ...opts.params,
      omniauth_window_type: omniauthWindowType
    };
    Object.entries(params).forEach(([key, val]) => authUrl.searchParams.append(key, val));
    return authUrl.toString();
  }
  requestCredentialsViaPostMessage(authWindow) {
    if (authWindow.closed) {
      return this.handleAuthWindowClose();
    }
    authWindow.postMessage("requestCredentials", "*");
    this.requestCredentialsPollingTimer = window.setTimeout(
      () => this.requestCredentialsViaPostMessage(authWindow),
      500
    );
    return this.requestCredentialsPollingTimer;
  }
  requestCredentialsViaExecuteScript(authWindow) {
    var _a;
    (_a = this.cancelOmniauthInAppBrowserListeners) == null ? void 0 : _a.call(this);
    const handleAuthWindowClose = this.handleAuthWindowClose.bind(this);
    const handleLoadStop = this.handleLoadStop.bind(this, authWindow);
    const handlePostMessage = this.handlePostMessage.bind(this);
    authWindow.addEventListener("loadstop", handleLoadStop);
    authWindow.addEventListener("exit", handleAuthWindowClose);
    authWindow.addEventListener("message", handlePostMessage);
    this.cancelOmniauthInAppBrowserListeners = () => {
      authWindow.removeEventListener("loadstop", handleLoadStop);
      authWindow.removeEventListener("exit", handleAuthWindowClose);
      return authWindow.addEventListener("message", handlePostMessage);
    };
    return this.cancelOmniauthInAppBrowserListeners;
  }
  handleLoadStop(authWindow) {
    const _this = this;
    const remoteCode = `function performBestTransit() { var data = requestCredentials(); if (webkit && webkit.messageHandlers && webkit.messageHandlers.cordova_iab) { var dataWithDeliverMessage = Object.assign({}, data, { message: 'deliverCredentials' }); webkit.messageHandlers.cordova_iab.postMessage(JSON.stringify(dataWithDeliverMessage)); return 'postMessageSuccess'; } else { return data; } } performBestTransit();`;
    return authWindow.executeScript({ code: remoteCode }, (response) => {
      var _a;
      const data = response[0];
      if (data === "postMessageSuccess") {
        return authWindow.close();
      }
      if (data) {
        const ev = new Event("message");
        ev.data = data;
        (_a = _this.cancelOmniauthInAppBrowserListeners) == null ? void 0 : _a.call(_this);
        window.dispatchEvent(ev);
        _this.initDfd();
        return authWindow.close();
      }
    });
  }
  handleAuthWindowClose() {
    var _a;
    this.cancel({
      reason: "unauthorized",
      errors: ["User canceled login"]
    });
    (_a = this.cancelOmniauthInAppBrowserListeners) == null ? void 0 : _a.call(this);
    this.getConfig().broadcast("auth:window-closed");
  }
  resolveDfd() {
    if (!this.dfd) {
      return void 0;
    }
    this.dfd.resolve(this.user);
    return new Promise((resolve) => {
      window.setTimeout(() => {
        this.dfd = null;
        resolve(null);
      });
    });
  }
  buildQueryString(params, prefix) {
    const str = [];
    Object.entries(params).forEach(([key, val]) => {
      const k = prefix ? `${prefix}[${key}]` : key;
      const encoded = isObject(val) ? this.buildQueryString(val, k) : `${k}=${encodeURIComponent(val)}`;
      str.push(encoded);
    });
    return str.join("&");
  }
  parseQueryString(searchString) {
    const queryString = searchString.substring(1);
    const params = {};
    if (queryString) {
      const pairs = queryString.split("&");
      pairs.forEach((pair) => {
        if (pair === "" || typeof pair === "function") {
          return;
        }
        const [key, val] = pair.split("=");
        params[decodeURIComponent(key)] = decodeURIComponent(val);
      });
    }
    return params;
  }
  validateUser(opts = {}) {
    let configName = opts.config;
    if (this.dfd == null) {
      const promise = this.initDfd();
      if (this.getConfig(configName).transport === "headers" && this.userIsAuthenticated()) {
        this.resolveDfd();
      } else {
        const params = this.parseQueryString(window.location.search);
        const token = params.auth_token || params.token;
        if (token !== void 0) {
          const clientId = params.client_id;
          const { uid, expiry } = params;
          configName = params.config;
          this.setConfigName(configName);
          this.mustResetPassword = params.reset_password;
          this.firstTimeLogin = params.account_confirmation_success;
          this.oauthRegistration = params.oauth_registration;
          this.setAuthHeaders(this.buildAuthHeaders({ token, clientId, uid, expiry }));
          let url = window.location.pathname;
          [
            "auth_token",
            "token",
            "client_id",
            "uid",
            "expiry",
            "config",
            "reset_password",
            "account_confirmation_success",
            "oauth_registration"
          ].forEach((prop) => delete params[prop]);
          if (Object.keys(params).length > 0) {
            url += `?${this.buildQueryString(params)}`;
          }
          this.getConfig(configName).navigate(url);
        } else if (this.retrieveData("currentConfigName")) {
          configName = this.retrieveData("currentConfigName");
        }
        if (this.getConfig().forceValidateToken) {
          this.validateToken({ config: configName });
        } else if (this.getConfig(configName).transport === "headers" && this.retrieveData("auth_headers")) {
          if (this.tokenHasExpired()) {
            this.getConfig(configName).broadcast("auth:session-expired");
            this.rejectDfd({
              reason: "unauthorized",
              errors: ["Session expired."]
            });
          } else {
            this.validateToken({ config: configName });
          }
        } else if (this.getConfig(configName).transport === "cookies") {
          this.validateToken({ config: configName });
        } else {
          this.rejectDfd({
            reason: "unauthorized",
            errors: ["No credentials"]
          });
          this.getConfig(configName).broadcast("auth:invalid");
        }
      }
      return promise;
    }
    return this.dfd.promise;
  }
  validateToken(opts = {}) {
    if (!this.tokenHasExpired()) {
      const config = this.getConfig(opts.config);
      return this.http(this.apiUrl(opts.config) + config.tokenValidationPath).then(
        (resp) => {
          const authData = config.handleTokenValidationResponse(resp.data);
          this.handleValidAuth(authData);
          if (this.firstTimeLogin) {
            config.broadcast("auth:email-confirmation-success", this.user);
          }
          if (this.oauthRegistration) {
            config.broadcast("auth:oauth-registration", this.user);
          }
          if (this.mustResetPassword) {
            config.broadcast("auth:password-reset-confirm-success", this.user);
          }
          config.broadcast("auth:validation-success", this.user);
          return resp;
        },
        (resp) => {
          if (this.firstTimeLogin) {
            config.broadcast("auth:email-confirmation-error", resp.data);
          }
          if (this.mustResetPassword) {
            config.broadcast("auth:password-reset-confirm-error", resp.data);
          }
          config.broadcast("auth:validation-error", resp.data);
          const invalidateTokens = resp.status > 0;
          return this.rejectDfd(
            {
              reason: "unauthorized",
              errors: resp.data ? resp.data.errors : ["Unspecified error"]
            },
            invalidateTokens
          );
        }
      );
    }
    return this.rejectDfd({
      reason: "unauthorized",
      errors: ["Expired credentials"]
    });
  }
  tokenHasExpired() {
    const expiry = this.getExpiry();
    const now = new Date().getTime();
    return expiry !== null && expiry < now;
  }
  getExpiry() {
    return this.getConfig().parseExpiry(this.retrieveData("auth_headers") || {});
  }
  invalidateTokens() {
    for (const key in this.user) {
      delete this.user[key];
    }
    this.deleteData("currentConfigName");
    if (this.timer != null) {
      window.clearInterval(this.timer);
    }
    return this.deleteData("auth_headers");
  }
  signOut(opts = {}) {
    const config = this.getConfig(opts.config);
    return this.http(this.apiUrl(opts.config) + config.signOutUrl, { method: "DELETE" }).then(
      (resp) => {
        this.invalidateTokens();
        config.broadcast("auth:logout-success");
        return resp;
      },
      (resp) => {
        this.invalidateTokens();
        config.broadcast("auth:logout-error", resp.data);
        throw resp;
      }
    );
  }
  handleValidAuth(user, setHeaders = false) {
    var _a;
    if (setHeaders == null) {
      setHeaders = false;
    }
    if (this.requestCredentialsPollingTimer != null) {
      window.clearTimeout(this.requestCredentialsPollingTimer);
    }
    (_a = this.cancelOmniauthInAppBrowserListeners) == null ? void 0 : _a.call(this);
    Object.assign(this.user, user);
    this.user.signedIn = true;
    this.user.configName = this.getCurrentConfigName();
    if (setHeaders) {
      this.setAuthHeaders(
        this.buildAuthHeaders({
          token: this.user.auth_token,
          clientId: this.user.client_id,
          uid: this.user.uid,
          expiry: this.user.expiry
        })
      );
    }
    return this.resolveDfd();
  }
  buildAuthHeaders(ctx, opts = {}) {
    const headers = {};
    const tokenFormat = this.getConfig(opts.config).tokenFormat;
    Object.entries(tokenFormat).forEach(([key, val]) => {
      headers[key] = interpolate(val, ctx);
    });
    return headers;
  }
  persistData(key, val, configName) {
    const { storage, transport, cookieOps } = this.getConfig(configName);
    if (transport === "cookies") {
      return void 0;
    }
    if (storage instanceof Object) {
      return storage.persistData(key, val);
    }
    switch (storage) {
      case "localStorage":
        return window.localStorage.setItem(key, JSON.stringify(val));
      case "sessionStorage":
        return window.sessionStorage.setItem(key, JSON.stringify(val));
      default:
        return Cookies.set(key, val, cookieOps);
    }
  }
  retrieveData(key) {
    const { storage, transport } = this.getConfig();
    if (transport === "cookies") {
      return void 0;
    }
    try {
      if (storage instanceof Object) {
        return storage.retrieveData(key);
      }
      switch (storage) {
        case "localStorage":
          return JSON.parse(window.localStorage.getItem(key) || "null");
        case "sessionStorage":
          return JSON.parse(window.sessionStorage.getItem(key) || "null");
        default:
          return JSON.parse(Cookies.get(key) || "null");
      }
    } catch (e) {
      if (e instanceof SyntaxError) {
        return null;
      }
      throw e;
    }
  }
  deleteData(key) {
    const { storage, transport, cookieOps } = this.getConfig();
    if (transport === "cookies") {
      return void 0;
    }
    if (storage instanceof Object) {
      storage.deleteData(key);
    }
    switch (storage) {
      case "localStorage":
        return window.localStorage.removeItem(key);
      case "sessionStorage":
        return window.sessionStorage.removeItem(key);
      default: {
        const options = { path: cookieOps.path };
        if (cookieOps.domain !== void 0) {
          options.domain = cookieOps.domain;
        }
        return Cookies.remove(key, options);
      }
    }
  }
  setAuthHeaders(headers) {
    const newHeaders = {
      ...this.retrieveData("auth_headers") || {},
      ...headers
    };
    this.persistData("auth_headers", newHeaders);
    const expiry = this.getExpiry();
    const now = new Date().getTime();
    if (expiry && expiry > now) {
      if (this.timer != null) {
        window.clearInterval(this.timer);
      }
      this.timer = window.setInterval(
        () => this.validateUser({ config: this.getSavedConfig() }),
        expiry - now
      );
    }
  }
  initDfd() {
    let resolve, reject;
    const promise = new Promise((_resolve, _reject) => {
      resolve = _resolve;
      reject = _reject;
    });
    this.dfd = {
      promise,
      resolve,
      reject
    };
    return this.dfd.promise;
  }
  rejectDfd(reason, invalidateTokens = true) {
    if (invalidateTokens) {
      this.invalidateTokens();
    }
    if (this.dfd != null) {
      this.dfd.reject(reason);
      return new Promise((resolve) => {
        window.setTimeout(() => {
          this.dfd = null;
          resolve(null);
        });
      });
    }
    return void 0;
  }
  apiUrl(configName) {
    const config = this.getConfig(configName);
    if (config.proxyIf()) {
      return config.proxyUrl;
    }
    return config.apiUrl;
  }
  getConfig(name) {
    return configs[this.getCurrentConfigName(name)];
  }
  getResultOrValue(arg) {
    if (typeof arg === "function") {
      return arg();
    }
    return arg;
  }
  getCurrentConfigName(name) {
    return name || this.getSavedConfig();
  }
  getSavedConfig() {
    let c = null;
    const key = "currentConfigName";
    if (this.hasLocalStorage() && c == null) {
      c = JSON.parse(window.localStorage.getItem(key) || "null");
    } else if (this.hasSessionStorage() && c == null) {
      c = JSON.parse(window.sessionStorage.getItem(key) || "null");
    } else if (c == null) {
      c = Cookies.get(key) || "null";
    }
    return c || defaultConfigName;
  }
  hasSessionStorage() {
    if (this._hasSessionStorage == null) {
      this._hasSessionStorage = false;
      try {
        window.sessionStorage.setItem("DeviceTokenAuthClient-test", "DeviceTokenAuthClient-test");
        window.sessionStorage.removeItem("DeviceTokenAuthClient-test");
        this._hasSessionStorage = true;
      } catch (error) {
        this._hasSessionStorage = false;
      }
    }
    return this._hasSessionStorage;
  }
  hasLocalStorage() {
    if (this._hasLocalStorage == null) {
      this._hasLocalStorage = false;
      try {
        window.localStorage.setItem("DeviceTokenAuthClient-test", "DeviceTokenAuthClient-test");
        window.localStorage.removeItem("DeviceTokenAuthClient-test");
        this._hasLocalStorage = true;
      } catch (error) {
        this._hasLocalStorage = false;
      }
    }
    return this._hasLocalStorage;
  }
};
export {
  DeviseTokenAuthClient as default
};
